import { useTranslation } from "react-i18next";
import { useCallback, useState, useEffect } from "react";
import Dialog from "components/common/Dialog/Dialog";
import Button from "components/common/Button/Button";
import {
  ContainerButtonStyle,
  DeleteButtonsStyle,
  InputStyle,
} from "./AddTax.style";
import {
  ITaxAddDialog,
  ITaxAddingData,
  ITaxesAddingDataError,
} from "./AddTax.types";
import { useSnackBar } from "hooks/useSnackBar";
import TextInput from "components/common/TextInput/TextInput";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useAddTaxMutation } from "redux/api/tax/taxes";
import countries from "./countries.json"; // Import countries.json

function AddTax({ openAddingDialog, setOpenAddingDialog }: ITaxAddDialog) {
  const { t } = useTranslation();
  const { displaySuccessSnackBar, displayErrorApiSnackBar } = useSnackBar();

  const [addingData, setAddingData] = useState<ITaxAddingData>({
    name: "",
    percentage: "",
    country: null,
  });

  const [addingError, setAddingError] = useState<ITaxesAddingDataError>({
    name: "",
    percentage: "",
    country: "",
  });
function convertUnicodeToEmoji(unicode: string) {
  return unicode
    .split(' ')
    .map(u => String.fromCodePoint(parseInt(u.replace('U+', ''), 16)))
    .join('');
}

  const [addingTax, { data, error, isLoading, reset }] = useAddTaxMutation();

  if (error) {
    displayErrorApiSnackBar(error);
    reset();
  }
  if (data) {
    displaySuccessSnackBar(t("tax.successfully_adding"));
    reset();
  }

  const closeAddingDialog = useCallback(() => {
    setOpenAddingDialog(false);
    setAddingData({ name: "", percentage: "", country: null });
    setAddingError({ name: "", percentage: "", country: "" });
  }, [setOpenAddingDialog]);

  const onSuccessAddingDialog = (event: React.FormEvent) => {
    event.preventDefault();
    if (Object.values(addingError).every((err) => err === "")) {
      addingTax(addingData);
      closeAddingDialog();
    }
  };

  useEffect(() => {
    console.log("addingData", addingData);
  }, [addingData]);

  const onChange = useCallback(
    (field: string) => (value: any) => {
      if (field === "country" && value && typeof value === "object") {
        value = value.name_fr; 
      }

      setAddingData((prevData) => ({
        ...prevData,
        [field]: value,
      }));

      // Validation for the country field
      if (field === "country" && value === "") {
        setAddingError((prevError) => ({
          ...prevError,
          country: "Pays obligatoire",
        }));
      } else {
        setAddingError((prevError) => ({
          ...prevError,
          country: "",
        }));
      }
    },
    []
  );

  return (
    <Dialog
      open={openAddingDialog}
      handleClose={closeAddingDialog}
      title={t("tax.add")}
    >
      <form onSubmit={onSuccessAddingDialog}>
        <InputStyle>
          <TextInput
            value={addingData.name}
            label={t("tax.name")}
            onChange={(e) => onChange("name")(e.target.value)}
            error={t(addingError.name)}
          />
        </InputStyle>
        <InputStyle>
          <TextInput
            value={addingData.percentage}
            label={t("tax.percentage")}
            type="number"
            onChange={(e) => onChange("percentage")(e.target.value)}
            error={t(addingError.percentage)}
          />
        </InputStyle>
        <InputStyle>
          <FormControl fullWidth error={!!addingError.country}>
            <Autocomplete
              options={countries}
              value={
                countries.find(
                  (country) => country.name_fr === addingData.country?.name_fr
                ) || null
              }
              onChange={(event, newValue) => onChange("country")(newValue)}
              getOptionLabel={(option) => (option ? option.name_fr : "")}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                   {convertUnicodeToEmoji(option.emojiU)} {option.name_fr}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("tax.country")}
                  error={!!addingError.country}
                  helperText={addingError.country && t(addingError.country)}
                  variant="outlined"
                />
              )}
            />
          </FormControl>
        </InputStyle>

        <DeleteButtonsStyle>
          <ContainerButtonStyle>
            <Button
              disabled={isLoading}
              title={t("common.cancel_button")}
              onClick={closeAddingDialog}
            />
          </ContainerButtonStyle>
          <ContainerButtonStyle>
            <Button
              disabled={isLoading}
              type="submit"
              loader={isLoading}
              title={t("common.confirm_button")}
            />
          </ContainerButtonStyle>
        </DeleteButtonsStyle>
      </form>
    </Dialog>
  );
}

export default AddTax;
